import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { C, serif, mono, EASE } from "@/lib/theme";
import { useT } from "@/lib/i18n";

const LINES = ["One Developer.", "Business Consultant.", "Complete Ownership.", "Since 2019."];
const MIN_MS = 900; // minimum on-screen time — kept short so it doesn't gate LCP

const Preloader = ({ onDone }) => {
  const t = useT();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const start = Date.now();
    let finished = false;
    const finish = () => { if (finished) return; finished = true; setLeaving(true); };
    const onLoad = () => window.setTimeout(finish, Math.max(0, MIN_MS - (Date.now() - start)));
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad);
    const cap = window.setTimeout(finish, 1800); // safety cap so it never hangs
    return () => { window.removeEventListener("load", onLoad); window.clearTimeout(cap); };
  }, []);

  return (
    <AnimatePresence onExitComplete={onDone}>
      {!leaving && (
        <motion.div
          key="preloader"
          data-testid="preloader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.75, ease: EASE } }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 500,
            background: C.dark,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "clamp(24px, 7vw, 120px)",
            overflow: "hidden",
          }}
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            style={{ position: "absolute", top: "clamp(20px, 4vw, 46px)", left: "clamp(24px, 7vw, 120px)", fontFamily: mono, fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: C.mut70 }}
          >
            Atelier Williams Studio
          </motion.span>

          <div>
            {LINES.map((line, i) => (
              <div key={line} style={{ overflow: "hidden", padding: "0.04em 0" }}>
                <motion.p
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.55, ease: EASE, delay: 0.08 + i * 0.11 }}
                  style={{
                    margin: 0,
                    fontFamily: serif,
                    fontWeight: 400,
                    fontSize: "clamp(32px, 6vw, 90px)",
                    lineHeight: 1.02,
                    letterSpacing: "-0.02em",
                    color: i === LINES.length - 1 ? C.accent : C.cream98,
                  }}
                >
                  {t(line)}
                </motion.p>
              </div>
            ))}
          </div>

          <motion.div
            aria-hidden="true"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: MIN_MS / 1000, ease: "linear" }}
            style={{ position: "absolute", left: 0, bottom: 0, height: 2, width: "100%", background: C.accent, transformOrigin: "left" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
